import {
  BatchStatus,
  type BulkTicketSale,
  type BulkTicketSaleBatch,
  type BulkTicketSaleBatchDownloadUrl,
  type BulkTicketSaleBatchProgress,
  type FetchAllTicketSaleTargetListParams,
  type FetchTicketSaleListParams,
  type FetchTicketSaleTargetListParams,
  type TicketSale,
  type TicketSaleTarget
} from 'Services/SalesManagement/Features/TicketSale/types'
import type { ListResponse } from 'Services/SalesManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'
import WebsocketUrl from 'Util/WebsocketUrl'

enum TagTypes {
  TICKET_SALE = 'TicketSale',
  TICKET_SALE_TARGET = 'TicketSaleTarget',
  BULK_TICKET_SALE = 'BulkTicketSale',
  BULK_TICKET_SALE_BATCH = 'BulkTicketSaleBatch'
}

export const ticketSaleApi = createBaseApi({
  reducerPath: 'ticketSaleApi',
  keepUnusedDataFor: 10,
  tagTypes: [
    TagTypes.TICKET_SALE,
    TagTypes.TICKET_SALE_TARGET,
    TagTypes.BULK_TICKET_SALE,
    TagTypes.BULK_TICKET_SALE_BATCH
  ]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getTicketSales: builder.query<ListResponse<TicketSale>, FetchTicketSaleListParams | void>({
      query: params => ({
        url: '/api/v1/ticket-sales/group-tickets/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TICKET_SALE, id })),
              { type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    getSingleTicketSale: builder.query<TicketSale, string>({
      query: id => `/api/v1/ticket-sales/group-tickets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TICKET_SALE, id }]
    }),
    createSingleTicketSale: builder.mutation<TicketSale, Partial<TicketSale>>({
      query: data => ({
        url: `/api/v1/ticket-sales/group-tickets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    sendTicketSaleEmailReceipt: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/ticket-sales/group-tickets/${id}/receipt/`,
        method: 'POST'
      })
    }),
    getTicketSaleTargets: builder.query<
      ListResponse<TicketSaleTarget>,
      FetchTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TICKET_SALE_TARGET, id })),
              { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST }]
    }),
    getAllTicketSaleTargets: builder.query<
      TicketSaleTarget[],
      FetchAllTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.TICKET_SALE_TARGET, id })),
              { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
            ]
          : [{ type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }]
    }),
    getSingleTicketSaleTarget: builder.query<TicketSaleTarget, string>({
      query: id => `/api/v1/ticket-sales/targets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TICKET_SALE_TARGET, id }]
    }),
    createSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.TICKET_SALE_TARGET, id },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleTicketSaleTarget: builder.mutation<void, string>({
      query: id => ({
        url: `/api/v1/ticket-sales/targets/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    getBulkTicketSales: builder.query<ListResponse<BulkTicketSale>, FetchTicketSaleListParams>({
      query: params => ({
        url: '/api/v1/ticket-sales/bulk-sales/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.BULK_TICKET_SALE, id })),
              { type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    getSingleBulkTicketSale: builder.query<BulkTicketSale, string>({
      query: id => `/api/v1/ticket-sales/bulk-sales/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.BULK_TICKET_SALE, id }]
    }),
    createBulkTicketSale: builder.mutation<BulkTicketSale, Partial<BulkTicketSale>>({
      query: data => ({
        url: `/api/v1/ticket-sales/bulk-sales/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    getBulkTicketSaleBatches: builder.query<BulkTicketSaleBatch[], string>({
      query: bulkSaleId => ({
        url: `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/`
      }),
      providesTags: results =>
        results
          ? [
              ...results.map(({ id }) => ({ type: TagTypes.BULK_TICKET_SALE_BATCH, id })),
              { type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST }]
    }),
    getSingleBulkTicketSaleBatch: builder.query<
      BulkTicketSaleBatch,
      { bulkSaleId: string; batchId: string }
    >({
      query: ({ bulkSaleId, batchId }) =>
        `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/${batchId}/`,
      providesTags: (_result, _error, { batchId }) => [
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: batchId }
      ]
    }),
    createBulkTicketSaleBatch: builder.mutation<
      BulkTicketSaleBatch,
      { bulkSaleId: string; requested_quantity: number }
    >({
      query: ({ bulkSaleId, requested_quantity }) => ({
        url: `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/`,
        method: 'POST',
        body: { requested_quantity }
      }),
      invalidatesTags: (_result, _error, { bulkSaleId }) => [
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST },
        { type: TagTypes.BULK_TICKET_SALE, id: bulkSaleId },
        { type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }
      ]
    }),
    getBulkTicketSaleBatchProgress: builder.query<
      BulkTicketSaleBatchProgress,
      { bulkSaleId: string; batchId: string }
    >({
      keepUnusedDataFor: 0,
      queryFn: () => ({
        data: {
          status: BatchStatus.QUEUED,
          processed_count: 0,
          requested_quantity: 0,
          travel_account_count: 0,
          cancelled_count: 0,
          estimated_seconds: 0
        }
      }),
      async onCacheEntryAdded(
        { bulkSaleId, batchId },
        { updateCachedData, cacheEntryRemoved, cacheDataLoaded, dispatch }
      ) {
        let ws: WebSocket | undefined
        let previousStatus: BatchStatus | undefined

        try {
          await cacheDataLoaded

          ws = new WebSocket(
            WebsocketUrl(`ticket-sales/bulk-sales/${bulkSaleId}/batches/${batchId}/`)
          )

          ws.onmessage = event => {
            try {
              const message = JSON.parse(event.data) as BulkTicketSaleBatchProgress
              updateCachedData(() => message)
              // Invalidate cache on status transitions
              const isStatusTransition =
                previousStatus !== undefined && message.status !== previousStatus
              previousStatus = message.status
              if (isStatusTransition) {
                dispatch(
                  ticketSaleApi.util.invalidateTags([
                    { type: TagTypes.BULK_TICKET_SALE, id: bulkSaleId },
                    { type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST },
                    { type: TagTypes.BULK_TICKET_SALE_BATCH, id: batchId },
                    { type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST }
                  ])
                )
              }
            } catch (e) {
              console.error(
                'Failed to parse WebSocket message for bulk ticket sale batch progress',
                e
              )
            }
          }

          await cacheEntryRemoved
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        } finally {
          ws?.close()
        }
      }
    }),
    cancelBulkTicketSaleBatch: builder.mutation<void, { bulkSaleId: string; batchId: string }>({
      query: ({ bulkSaleId, batchId }) => ({
        url: `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/${batchId}/cancel/`,
        method: 'POST',
        body: {}
      }),
      invalidatesTags: (_result, _error, { bulkSaleId, batchId }) => [
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: batchId },
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST },
        { type: TagTypes.BULK_TICKET_SALE, id: bulkSaleId },
        { type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }
      ]
    }),
    retryBulkTicketSaleBatch: builder.mutation<void, { bulkSaleId: string; batchId: string }>({
      query: ({ bulkSaleId, batchId }) => ({
        url: `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/${batchId}/retry/`,
        method: 'POST',
        body: {}
      }),
      invalidatesTags: (_result, _error, { bulkSaleId, batchId }) => [
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: batchId },
        { type: TagTypes.BULK_TICKET_SALE_BATCH, id: RTKCacheTags.LIST },
        { type: TagTypes.BULK_TICKET_SALE, id: bulkSaleId },
        { type: TagTypes.BULK_TICKET_SALE, id: RTKCacheTags.LIST }
      ]
    }),
    getBulkTicketSaleBatchDownloadUrl: builder.query<
      BulkTicketSaleBatchDownloadUrl,
      { bulkSaleId: string; batchId: string }
    >({
      query: ({ bulkSaleId, batchId }) =>
        `/api/v1/ticket-sales/bulk-sales/${bulkSaleId}/batches/${batchId}/download-url/`
    })
  })
})

export const {
  useGetTicketSalesQuery,
  useGetSingleTicketSaleQuery,
  useCreateSingleTicketSaleMutation,
  useSendTicketSaleEmailReceiptMutation,
  useGetTicketSaleTargetsQuery,
  useGetAllTicketSaleTargetsQuery,
  useGetSingleTicketSaleTargetQuery,
  useCreateSingleTicketSaleTargetMutation,
  useUpdateSingleTicketSaleTargetMutation,
  useDeleteSingleTicketSaleTargetMutation,
  useGetBulkTicketSalesQuery,
  useGetSingleBulkTicketSaleQuery,
  useCreateBulkTicketSaleMutation,
  useGetBulkTicketSaleBatchesQuery,
  useGetSingleBulkTicketSaleBatchQuery,
  useCreateBulkTicketSaleBatchMutation,
  useGetBulkTicketSaleBatchProgressQuery,
  useCancelBulkTicketSaleBatchMutation,
  useRetryBulkTicketSaleBatchMutation,
  useLazyGetBulkTicketSaleBatchDownloadUrlQuery
} = ticketSaleApi
